import { type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LogOut, Radio } from "lucide-react";

import { useAuth } from "./auth";
import AcceptInvite from "./pages/AcceptInvite";
import Board from "./pages/Board";
import Cash from "./pages/Cash";
import DispatchRecovery from "./pages/DispatchRecovery";
import Economics from "./pages/Economics";
import Exceptions from "./pages/Exceptions";
import ForgotPassword from "./pages/ForgotPassword";
import Login from "./pages/Login";
import MapView from "./pages/Map";
import Notifications from "./pages/Notifications";
import Pilot from "./pages/Pilot";
import Reconciled from "./pages/Reconciled";
import Remedies from "./pages/Remedies";
import ResetPassword from "./pages/ResetPassword";
import RoutePlanner from "./pages/Routes";
import Risk from "./pages/Risk";
import StaffRemedies from "./pages/StaffRemedies";
import SsoExchange from "./pages/SsoExchange";
import StaffPilots from "./pages/StaffPilots";
import StaffVerification from "./pages/StaffVerification";
import Verification from "./pages/Verification";
import WorkList from "./pages/WorkList";

function Shell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <Radio size={18} className="brand-icon" />
          <span>
            VendorIQ <b>Operations</b>
          </span>
        </div>
        <nav className="topnav">
          {/* Dispatch: the live work — board (Activity), the fleet map, and the rare manual
              dispatch-recovery / route-action surface. */}
          <NavLink to="/" end className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Board
          </NavLink>
          <NavLink to="/map" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Map
          </NavLink>
          <NavLink to="/routes" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Routes
          </NavLink>
          <NavLink to="/work-list" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Work list
          </NavLink>
          <NavLink to="/dispatch-recovery" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Dispatch recovery
          </NavLink>
          <span className="nav-sep" aria-hidden="true" />
          {/* Exceptions: the CASE-01 inbox — catalog/order/statement/payout issues, owned. */}
          <NavLink to="/exceptions" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Exceptions
          </NavLink>
          <NavLink to="/risk" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Risk
          </NavLink>
          <span className="nav-sep" aria-hidden="true" />
          {/* Money: cash custody, the reconciled read model and the VERIFY-01 reconciliation oracles. */}
          <NavLink to="/cash" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Cash
          </NavLink>
          {/* AP-20: reconciled money reads are owner/admin only (the API refuses other roles). */}
          {(user?.role === "owner" || user?.role === "admin") && (
            <NavLink to="/reconciled" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
              Reconciled
            </NavLink>
          )}
          <NavLink to="/verification" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Verification
          </NavLink>
          {/* MOAT-01: the measured pilot governing this shop, if any (read-only; owner/admin may disable). */}
          <NavLink to="/pilot" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Pilot
          </NavLink>
          {(user?.role === "owner" || user?.role === "admin") && (
            <NavLink to="/billing-reviews" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
              Billing reviews
            </NavLink>
          )}
          <span className="nav-sep" aria-hidden="true" />
          {/* Sync/help: the NOT-01 notification DLQ + operator reconciliation job. */}
          <NavLink to="/notifications" className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
            Notifications
          </NavLink>
        </nav>
        <div className="shop">{user?.shop_name || user?.shop_slug}</div>
        <div className="spacer" />
        <div className="who">
          <span className={`role role-${user?.role}`}>{user?.role}</span>
          <span className="who-name">{user?.display_name}</span>
        </div>
        <button className="logout" onClick={() => void logout()} title="Sign out">
          <LogOut size={16} />
        </button>
      </header>
      <main className="content">{children}</main>
    </div>
  );
}

function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="boot">Loading the operations console…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <Shell>{children}</Shell>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/sso-exchange" element={<SsoExchange />} />
      <Route path="/auth/accept-invite" element={<AcceptInvite />} />
      {/* Matches the reset-link path the backend mints: console_base_url() + "/auth/reset-password?token=..."
          (app/api/console_portal/auth.py forgot_password) — outside <Protected>, like every other auth route. */}
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      {/* Kernvel-staff pages (decided 2026-09-18): deliberately OUTSIDE <Protected> and the nav --
          gated by the Kernvel-staff authority (pages/StaffGate.tsx, a Kernvel-signed staff token),
          never an AdminUser session, so no merchant operator's login (of any role) reaches them. */}
      <Route path="/internal/economics" element={<Economics />} />
      <Route path="/internal/billing-reviews" element={<StaffRemedies />} />
      <Route path="/internal/pilots" element={<StaffPilots />} />
      <Route path="/internal/verification" element={<StaffVerification />} />
      <Route
        path="/"
        element={
          <Protected>
            <Board />
          </Protected>
        }
      />
      <Route
        path="/risk"
        element={
          <Protected>
            <Risk />
          </Protected>
        }
      />
      <Route
        path="/exceptions"
        element={
          <Protected>
            <Exceptions />
          </Protected>
        }
      />
      <Route
        path="/map"
        element={
          <Protected>
            <MapView />
          </Protected>
        }
      />
      <Route
        path="/routes"
        element={
          <Protected>
            <RoutePlanner />
          </Protected>
        }
      />
      <Route
        path="/work-list"
        element={
          <Protected>
            <WorkList />
          </Protected>
        }
      />
      <Route
        path="/reconciled"
        element={
          <Protected>
            <Reconciled />
          </Protected>
        }
      />
      <Route
        path="/pilot"
        element={
          <Protected>
            <Pilot />
          </Protected>
        }
      />
      <Route
        path="/verification"
        element={
          <Protected>
            <Verification />
          </Protected>
        }
      />
      <Route
        path="/cash"
        element={
          <Protected>
            <Cash />
          </Protected>
        }
      />
      <Route
        path="/notifications"
        element={
          <Protected>
            <Notifications />
          </Protected>
        }
      />
      <Route
        path="/dispatch-recovery"
        element={
          <Protected>
            <DispatchRecovery />
          </Protected>
        }
      />
      <Route
        path="/billing-reviews"
        element={
          <Protected>
            <Remedies />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
